<template>
  <fullpageform
    title="Field Settings"
    submitLabel="Save"
    cancelLabel="Cancel"
    @submit="submit"
    @cancel="cancel"
  >
    <div>
      <fieldset>
        <legend>Field Settings</legend>
        <div class="field">
          <label for="field_name">Name</label>
          <input
            type="text"
            id="field_name"
            class="label_margin_left"
            v-model="fieldName"
            ref="name"
          />
        </div>
      </fieldset>
    </div>

    <fieldset>
      <legend>Field Layout</legend>
      <div class="widget_margin_top">
        <field-layout-select
          :field="field"
          :fieldLayout="this.emittedFieldLayoutObj?.layout || this.fieldLayout"
          v-model="fieldLayout.id"
          @fieldLayoutChanged="fieldLayoutChanged"
        />
        <span class="field_layout_desc">
          Additional layouts can be created on the same field boundary to support crop rotation or
          other temporary irrigation changes.
        </span>
        <h2>Settings</h2>
        <div class="field">
          <label for="gross-applied-inches">Gross Applied Inches</label>
          <input
            type="number"
            id="gross-applied-inches"
            style="width: 5em"
            class="label_margin_left"
            v-model="grossAppliedInches"
          />
        </div>

        <div class="field">
          <label for="max-watering-hours">Max Watering Hours</label>
          <input
            type="number"
            id="max-watering-hours"
            style="width: 5em"
            class="label_margin_left"
            v-model="desiredWateringTimeInHours"
          />
        </div>

        <div class="field">
          <label for="furrow-spacing">Furrow Spacing (in)</label>
          <input
            type="number"
            id="furrow-spacing"
            style="width: 5em"
            class="label_margin_left"
            v-model="furrowSpacingInInches"
          />
        </div>

        <div class="field">
          <TypeSelect
            label="Crop Type"
            :options="['Choose an option', 'Corn', 'Soybeans', 'Cotton', 'Rice', 'Other']"
            v-model="cropType"
            :style="{ width: '50%' }"
            :required="false"
          />
        </div>
      </div>

      <div class="widget_margin_top">
        <h2>Flow Rate</h2>
        <flow-rate-select
          :class="flowRateInvalid ? 'invalid' : ''"
          v-model="flowRateId"
          :field="field"
          :fieldLayout="this.emittedFieldLayoutObj?.layout || this.fieldLayout"
          :irrigationSystems="irrigationSystems"
          @applyFlowRate="flowRateChanged"
          :showApplyFlowRateChanges="true"
          :lineStringForDistanceCalculation="fieldLineString"
        />
      </div>
    </fieldset>
    <div v-if="field.mode === FieldMode.Levees">
      <levee-elevation-form
        v-if="field.mode == FieldMode.Levees"
        ref="leveeElevation"
        class="widget_margin_top"
        :field="field"
      />
    </div>
  </fullpageform>
</template>

<script>
import { GeoUtil } from '@/geo/GeoUtil'
import { FieldMode } from '@/store/Dto'

import LeveeElevationForm from '@/components/maps/irrigation_systems/LeveeElevationForm'
import TypeSelect from '../../TypeSelect.vue'
export default {
  components: {
    LeveeElevationForm,
    TypeSelect
  },
  props: {
    farm: {
      type: Object,
      required: true
    },
    field: {
      type: Object,
      required: true
    },
    fieldLayout: {
      type: Object,
      required: true
    },
    irrigationSystems: {
      type: Array,
      required: true
    }
  },
  data: function () {
    return {
      flowRateId: null,
      FieldMode,
      fieldName: null,
      fieldLayoutName: null,
      grossAppliedInches: null,
      furrowSpacingInInches: null,
      desiredWateringTimeInHours: null,
      cropType: null,
      // soilType: null,
      emittedFlowrateObj: null,
      emittedFieldLayoutObj: null
    }
  },
  computed: {
    fieldLineString: (vm) => GeoUtil.LatLngs.toLineString(vm.fieldLayout.path),
    flowRateInvalid: (vm) => vm.flowRateId == null
  },
  methods: {
    async patchField() {
      const patches = []

      if (this.field.name != this.fieldName) {
        patches.push(this.buildFieldNamePatch())
      }

      if (this.emittedFieldLayoutObj.fieldLayoutChangedBoolean == true) {
        await this.$store.dispatch('setSelectedFieldLayout', this.emittedFieldLayoutObj.layout)
      }

      if (patches.length > 0) {
        await this.$store.dispatch('patchField', { field: this.field, patch: patches })
      }
    },
    async patchFieldLayout(relatedFieldLayout) {
      const patches = []

      if (this.$refs.leveeElevation) {
        patches.push(...this.$refs.leveeElevation.buildPatches())
      }

      if (this.emittedFlowrateObj) {
        const newFlowRateId = this.emittedFlowrateObj.flowRateId
        if (newFlowRateId !== relatedFieldLayout.primaryFlowRateId) {
          patches.push({
            op: 'replace',
            path: '/primaryFlowRateId',
            value: newFlowRateId
          })
        }
      }

      if (this.grossAppliedInches !== relatedFieldLayout.grossAppliedInches) {
        patches.push({
          op: Object.hasOwn(relatedFieldLayout, 'grossAppliedInches') ? 'replace' : 'add',
          path: '/grossAppliedInches',
          value: this.grossAppliedInches
        })
      }

      if (this.desiredWateringTimeInHours !== relatedFieldLayout.desiredWateringTimeInHours) {
        patches.push({
          op: Object.hasOwn(relatedFieldLayout, 'desiredWateringTimeInHours') ? 'replace' : 'add',
          path: '/desiredWateringTimeInHours',
          value: this.desiredWateringTimeInHours
        })
      }

      if (this.furrowSpacingInInches !== relatedFieldLayout.furrowSpacingInInches) {
        patches.push({
          op: Object.hasOwn(relatedFieldLayout, 'furrowSpacingInInches') ? 'replace' : 'add',
          path: '/furrowSpacingInInches',
          value: this.furrowSpacingInInches
        })
      }

      if (this.cropType !== relatedFieldLayout.cropType) {
        patches.push({
          op: Object.hasOwn(relatedFieldLayout, 'cropType') ? 'replace' : 'add',
          path: '/cropType',
          value: this.cropType
        })
      }

      // if (this.soilType !== relatedFieldLayout.soilType) {
      //   patches.push({
      //     op: Object.hasOwn(relatedFieldLayout, 'soilType') ? 'replace' : 'add',
      //     path: '/soilType',
      //     value: this.soilType
      //   })
      // }

      if (patches.length >= 1) {
        await this.$store.dispatch('patchFieldLayout', {
          field: this.field,
          fieldLayout: relatedFieldLayout,
          patch: patches
        })
      }
    },
    async patchIrrigationSystemPipePaths(relatedFieldLayout) {
      console.log('! patchIrrigationSystemPipePaths()')
      if (this.emittedFlowrateObj?.applyFlowRateBoolean) {
        let irrigationSystem = this.irrigationSystems.find(
          (is) => is.id === this.emittedFlowrateObj.irrigationSystemId
        )

        for (let pipePath of irrigationSystem.pipePaths) {
          if (
            pipePath.fieldId !== this.field.id ||
            pipePath.fieldLayoutId !== relatedFieldLayout.id
          ) {
            continue
          }

          const patch = {
            op: 'replace',
            path: `/flowRateId`,
            value: this.emittedFlowrateObj.flowRateId
          }

          console.dir(patch)

          await this.$store.dispatch('patchIrrigationSystemPipePath', {
            farm: this.farm,
            irrigationSystem,
            pipePath,
            patch
          })
        }
      }
    },
    submit: async function () {
      await this.patchField()

      // User might have changed the layout.
      // Do not patch relative to property 'fieldLayout' because it may have changed.
      const relatedFieldLayout = this.emittedFieldLayoutObj
        ? this.emittedFieldLayoutObj.layout
        : this.fieldLayout

      await this.patchFieldLayout(relatedFieldLayout)
      await this.patchIrrigationSystemPipePaths(relatedFieldLayout)

      this.$dialog.pop()
    },
    cancel: function () {
      this.$dialog.pop()
    },
    flowRateChanged(flowRateObj) {
      if (flowRateObj.flowRateId !== this.emittedFieldLayoutObj.layout.primaryFlowRateId) {
        this.emittedFlowrateObj = flowRateObj
      } else {
        this.emittedFlowrateObj = null
      }
    },
    fieldLayoutChanged(layoutObj) {
      this.flowRateId = layoutObj.layout.primaryFlowRateId
      this.emittedFieldLayoutObj = layoutObj
      this.emittedFlowrateObj = null // reset when layout changes

      this.grossAppliedInches = layoutObj.layout.grossAppliedInches
      this.desiredWateringTimeInHours = layoutObj.layout.desiredWateringTimeInHours
      this.furrowSpacingInInches = layoutObj.layout.furrowSpacingInInches
      this.cropType = layoutObj.layout.cropType || 'Choose an option'
      this.soilType = layoutObj.layout.soilType || 'Choose an option'
    },
    buildFieldNamePatch() {
      //something like this.
      return {
        op: 'name' in this.field ? 'replace' : 'add',
        path: '/name',
        value: this.fieldName
      }
    },
    buildLayoutIdPatch() {
      return {
        op: 'activeLayoutId' in this.field ? 'replace' : 'add',
        path: '/activeLayoutId',
        value: this.emittedFieldLayoutObj.layout.id
      }
    }
  },
  beforeMount() {
    this.fieldName = this.field.name
    this.flowRateId = this.fieldLayout.primaryFlowRateId
    this.grossAppliedInches = this.fieldLayout.grossAppliedInches
    this.desiredWateringTimeInHours = this.fieldLayout.desiredWateringTimeInHours
    this.furrowSpacingInInches = this.fieldLayout.furrowSpacingInInches
    this.cropType = this.fieldLayout.cropType || 'Choose an option'
    this.soilType = this.fieldLayout.soilType || 'Choose an option'
  },
  mounted() {
    this.fieldLayoutName = this.fieldLayout.name
  }
}
</script>

<style lang="css" scoped>
.fieldset-group {
  border: 2px solid var(--primary);
  padding: 10px;
}

.field {
  display: block;
  margin-top: var(--smaller-padding);
  width: 60%;
}

.fieldset-group-set {
  background-color: var(--primary);
  font-size: small;
  border-radius: 5px;
  padding: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
}

.add_edit_remove_layouts {
  display: inline-flex;
  flex-direction: column;
}

.layout_btns {
  margin: 0.2em;
}
.field_layout_desc {
  display: flex;
  flex-wrap: wrap;
  max-width: 27rem;
  font-size: 0.9em;
  color: var(--fg2);
  line-height: 1.5;
  margin-bottom: 1rem;
  font-style: italic;
}
</style>
